import { DashboardWidgetData } from 'features/widget/types'
import { InsightCount } from 'schemas/action/InsightCountSchema'
import { InsightData } from 'schemas/action/InsightDataSchema'
import TableSortOption, { TableSortOptionStrings } from 'enums/TableSortOption'
import { ColSortingConfigs } from 'components/visualization/Table/types'
import { InsightViewOptionStrings } from 'enums/InsightViewOption'
import { CountType, Language, MessageConst } from 'enums'
import i18n from 'i18n'
import { LanguageInsight } from '../../types'

const retrieveDataSet = (response: DashboardWidgetData<InsightCount>) =>
  response?.results?.[0]?.data?.insights?.[0]?.dataset?.[0]?.set

export const getNumberFormatter = () =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3
  })

export const getMaxDataValue = (set: LanguageInsight[] | InsightData[]) =>
  Math.max(...set.map((item) => item.value!))

export const handleProgressDisplay = (progress: InsightData['share']) => {
  if (typeof progress !== 'number') {
    return 0
  }
  return Math.round(progress)
}

export const formatPercentShare = (value?: number) => {
  if (value === undefined) {
    return MessageConst.EM_DASH
  }

  if (value < 0) {
    return MessageConst.EM_DASH
  }
  if (value < 1) {
    return '<1%'
  }
  return `${value}%`
}

/**
 * convert responses from runsearch to data displayed on table
 * @param searchResponses responses from runsearch
 */
export const convertToTableData = (searchResponses: DashboardWidgetData<InsightCount>[]) => {
  const rowData =
    searchResponses
      ?.map((response) => {
        const dataSet = retrieveDataSet(response)
        const totalCount = dataSet?.find((item: InsightData) => item.name === 'All')?.value
        const filteredDataSet = dataSet?.filter((item: InsightData) => item.name !== 'All') || []

        return filteredDataSet.map((item: InsightData) => ({
          name: item.name!,
          value: item.value!,
          share: totalCount ? Math.round((item.value! / totalCount) * 100) : 0
        }))
      })
      .flat() || []
  return rowData
}

export const getSortingConfigByTableSortOption = (
  tableSortOption: TableSortOptionStrings | undefined
): ColSortingConfigs<'name' | 'value' | 'share'> => {
  switch (tableSortOption) {
    case TableSortOption.ALPHABET_ASC:
      return {
        field: 'name',
        order: TableSortOption.ASCEND
      }
    case TableSortOption.ALPHABET_DSC:
      return {
        field: 'name',
        order: TableSortOption.DESCEND
      }
    case TableSortOption.VALUE_ASC:
      return {
        field: 'value',
        order: TableSortOption.ASCEND
      }
    case TableSortOption.VALUE_DSC:
      return {
        field: 'value',
        order: TableSortOption.DESCEND
      }
    case TableSortOption.INDEX_ASC:
      return {
        field: 'share',
        order: TableSortOption.ASCEND
      }
    case TableSortOption.INDEX_DSC:
      return {
        field: 'share',
        order: TableSortOption.DESCEND
      }
    default:
      return {
        field: 'value',
        order: TableSortOption.DESCEND
      }
  }
}

export const getColorSetting = (countType: InsightViewOptionStrings) => {
  switch (countType) {
    case CountType.POSTS:
      return 'metric.mainMetrics.post'
    case CountType.AUTHORS:
      return 'metric.mainMetrics.authors'
    default:
      return ''
  }
}

export const getLanguageLabel = (languageName: InsightData['name']) => {
  switch (languageName) {
    case Language.ENGLISH:
      return i18n.t('languageGroup.english')
    case Language.CHINESE:
      return i18n.t('languageGroup.chinese')
    case Language.FRENCH:
      return i18n.t('languageGroup.french')
    case Language.GERMAN:
      return i18n.t('languageGroup.german')
    case Language.ITALIAN:
      return i18n.t('languageGroup.italian')
    case Language.JAPANESE:
      return i18n.t('languageGroup.japanese')
    case Language.PORTUGUESE:
      return i18n.t('languageGroup.portuguese')
    case Language.SIMPLIFIED_CHINESE:
      return i18n.t('languageGroup.simplifiedChinese')
    case Language.SPANISH:
      return i18n.t('languageGroup.spanish')
    case Language.TRADITIONAL_CHINESE:
      return i18n.t('languageGroup.traditionalChinese')
    case Language.ALL:
      return i18n.t('languageGroup.allLanguages')
    case Language.AFRIKAANS:
      return i18n.t('languageGroup.afrikaans')
    case Language.ARABIC:
      return i18n.t('languageGroup.arabic')
    case Language.ARMENIAN:
      return i18n.t('languageGroup.armenian')
    case Language.BENGALI:
      return i18n.t('languageGroup.bengali')
    case Language.BOKMAL:
      return i18n.t('languageGroup.norwegian')
    case Language.BULGARIAN:
      return i18n.t('languageGroup.bulgarian')
    case Language.CANTONESE:
      return i18n.t('languageGroup.cantonese')
    case Language.CATALAN:
      return i18n.t('languageGroup.catalan')
    case Language.CROATIAN:
      return i18n.t('languageGroup.croatian')
    case Language.CZECH:
      return i18n.t('languageGroup.czech')
    case Language.DANISH:
      return i18n.t('languageGroup.danish')
    case Language.DUTCH:
      return i18n.t('languageGroup.dutch')
    case Language.ESTONIAN:
      return i18n.t('languageGroup.estonian')
    case Language.FINNISH:
      return i18n.t('languageGroup.finnish')
    case Language.GEORGIAN:
      return i18n.t('languageGroup.georgian')
    case Language.GREEK:
      return i18n.t('languageGroup.greek')
    case Language.HEBREW:
      return i18n.t('languageGroup.hebrew')
    case Language.HINDI:
      return i18n.t('languageGroup.hindi')
    case Language.HUNGARIAN:
      return i18n.t('languageGroup.hungarian')
    case Language.ICELANDIC:
      return i18n.t('languageGroup.icelandic')
    case Language.INDONESIAN:
      return i18n.t('languageGroup.malayIndonesian')
    case Language.KOREAN:
      return i18n.t('languageGroup.korean')
    case Language.LAO:
      return i18n.t('languageGroup.lao')
    case Language.LATVIAN:
      return i18n.t('languageGroup.latvian')
    case Language.LITHUANIAN:
      return i18n.t('languageGroup.lithuanian')
    case Language.MACEDONIAN:
      return i18n.t('languageGroup.macedonian')
    case Language.NYNORSK:
      return i18n.t('languageGroup.norwegianNynorsk')
    case Language.PUNJABI:
      return i18n.t('languageGroup.punjabi')
    case Language.POLISH:
      return i18n.t('languageGroup.polish')
    case Language.ROMANIAN:
      return i18n.t('languageGroup.romanian')
    case Language.ROMANIZED_HINDI_URDU:
      return i18n.t('languageGroup.romanizedHindiUrdu')
    case Language.RUSSIAN:
      return i18n.t('languageGroup.russian')
    case Language.SERBIAN:
      return i18n.t('languageGroup.bosnianCroatianSerbianLatin')
    case Language.SERBIAN_NEW:
      return i18n.t('languageGroup.serbianCyrillic')
    case Language.BURMESE:
      return i18n.t('languageGroup.burmese')
    case Language.KHMER:
      return i18n.t('languageGroup.khmer')
    case Language.MONGOLIAN:
      return i18n.t('languageGroup.mongolian')
    case Language.URDU:
      return i18n.t('languageGroup.urdu')
    case Language.SLOVAK:
      return i18n.t('languageGroup.slovak')
    case Language.SWEDISH:
      return i18n.t('languageGroup.swedish')
    case Language.TAGALOG:
      return i18n.t('languageGroup.tagalog')
    case Language.TAMIL:
      return i18n.t('languageGroup.tamil')
    case Language.TELUGU:
      return i18n.t('languageGroup.telugu')
    case Language.THAI:
      return i18n.t('languageGroup.thai')
    case Language.TURKISH:
      return i18n.t('languageGroup.turkish')
    case Language.UKRAINIAN:
      return i18n.t('languageGroup.ukrainian')
    case Language.VIETNAMESE:
      return i18n.t('languageGroup.vietnamese')
    case Language.UNKNOWN:
      return i18n.t('languageGroup.unclassified')
    default:
      return ''
  }
}
